import React, { useEffect, useMemo, useRef, useState } from 'react'
import { adminService } from '../../../services/admin'
import {
  apiErrorMessage,
  archiveLocation,
  clearLocationGeofence,
  deleteLocation,
  disableLocation,
  disableLocationGeofence,
  getLocationDeleteImpact,
  getLocationGeofenceDeleteImpact,
  purgeDemoLocation,
  restoreLocation,
  restoreLocationGeofence,
} from '../../../services/access'
import type { AccessibleLocation, GrantedClientLocation, LiveWorkerLocationDto, Organization, OrganizationBranding, OrganizationUnit, PaginationMeta, RelationshipAccessEntry } from '../../../types/access'
import { useI18n } from '../../../i18n/I18nContext'
import { useListCommands } from '../../../hooks/useListCommands'
import { Mapper } from '../Mapper'
import { formatOrgType } from './adminPanelUtils'
import { BreadcrumbTrailViewer } from './BreadcrumbTrailViewer'
import { ShiftHistoryViewer } from './ShiftHistoryViewer'
import { BrandBadge, findBrand } from './BrandDisplay'
import {
  AdminActiveFilterChips,
  AdminFilterBar,
  AdminPage,
  AdminPageHeader,
  AdminRowActionMenu,
  AdminStatusBadge,
  AdminTableContainer,
  ConfirmDialog,
  DetailDrawer,
  EmptyState,
  EntityActionMenu,
  ErrorBanner,
  FormModal,
  LoadingState,
  PaginationControls,
  SortableHeader,
  type AdminRowAction,
  type AdminStatusTone,
} from './AdminUiPrimitives'
import { useAccessContext } from '../../../context/AccessContext'
import { useAdminListState } from '../../../context/AdminListStateContext'
import { useSmartTable } from '../../../hooks/useSmartTable'
import { locationTableDefinition, type LocationTableFilters } from './productivityTables'
import { TableDisplayControls, TableSortControls } from '../../ui/SmartTableControls'
import { useIndustryTerminology } from '../../../hooks/useIndustryTerminology'
import { LOCATION_COLUMNS } from './locationColumns'
import { locationDisplayAddress, normalizeLocationAddress } from '../../../services/locationAddress'
import { shouldReconcileLocationAccess } from './liveMapState'
import { createLocationComparator } from './locationSort'

type TFunc = (text: string, vars?: Record<string, string | number>) => string

type LocationForm = {
  name: string
  address: string
  city: string
  stateProvinceRegion: string
  postalCode: string
  countryCode: string
  latitude: string
  longitude: string
  timezone: string
  ownerOrgId: string
  requireShift: boolean
  activeTracking: boolean
}

type LocationMessageRow = {
  id: string
  sentAt?: string | null
  workerEmail?: string | null
  workerPhone?: string | null
  body?: string | null
  deliveryState?: string | null
}

type LocationTimesheetRow = {
  shiftId: string
  workerName?: string | null
  workerEmail?: string | null
  date?: string | null
  punchIn?: string | null
  punchOut?: string | null
  paidHours?: number | string | null
  status?: string | null
}

// Access-context refreshes remount the whole Admin workspace (a loading panel
// replaces it while /api/access/my-context reloads), so panel state cannot
// carry the create → configure-geofence handoff. This module-scoped intent
// survives the remount; it is honored only briefly and only once the created
// location is visible in the refreshed access context.
let pendingGeofenceIntent: { locationId: string; at: number } | null = null
const PENDING_GEOFENCE_INTENT_TTL_MS = 30_000
// Access-context refreshes currently remount AdminWorkspace. Keep only the
// user's explicit live-map visibility intent across that remount; live data is
// re-fetched and never controls this value.
interface LiveMapIntent { version: 1; scope: string; kind: 'owned' | 'granted'; locationId: string }
let preservedLiveMapIntent: LiveMapIntent | null = null
let liveMapIntentLoaded = false
const LIVE_MAP_INTENT_KEY = 'punch.admin.liveMapIntent'
const readLiveMapIntent = (scope: string | null): LiveMapIntent | null => {
  let intent: unknown = preservedLiveMapIntent
  if (!liveMapIntentLoaded && typeof window !== 'undefined') {
    liveMapIntentLoaded = true
    try {
      const raw = window.sessionStorage.getItem(LIVE_MAP_INTENT_KEY)
      intent = raw && raw.length <= 2000 ? JSON.parse(raw) : null
    } catch { /* Optional session intent. */ }
  }
  const value = intent as Partial<LiveMapIntent> | null
  if (scope && value?.version === 1 && value.scope === scope && ['owned', 'granted'].includes(value.kind || '')
    && typeof value.locationId === 'string' && value.locationId.length > 0 && value.locationId.length <= 200) {
    preservedLiveMapIntent = { version: 1, scope, kind: value.kind as LiveMapIntent['kind'], locationId: value.locationId }
    return preservedLiveMapIntent
  }
  // Reject old payload-bearing schemas and changed identity/tenant/org contexts.
  preservedLiveMapIntent = null
  try { window.sessionStorage.removeItem(LIVE_MAP_INTENT_KEY) } catch { /* Optional persistence. */ }
  return null
}
const writeLiveMapIntent = (intent: LiveMapIntent | null) => {
  liveMapIntentLoaded = true
  preservedLiveMapIntent = intent
  if (typeof window === 'undefined') return
  try {
    if (intent) window.sessionStorage.setItem(LIVE_MAP_INTENT_KEY, JSON.stringify(intent))
    else window.sessionStorage.removeItem(LIVE_MAP_INTENT_KEY)
  } catch { /* Keep the mounted visibility intent when optional storage fails. */ }
}

const locationName = (location: AccessibleLocation) => location.name || location.label || location.location_key || 'Unnamed location'
const locationAddress = (location: AccessibleLocation) => locationDisplayAddress(location)

type AddressBaseline = {
  line1: string
  city: string
  region: string
  postalCode: string
  countryCode: string
  latitude: string
  longitude: string
}

const emptyAddressBaseline: AddressBaseline = { line1: '', city: '', region: '', postalCode: '', countryCode: '', latitude: '', longitude: '' }

const addressBaselineFromForm = (form: LocationForm): AddressBaseline => ({
  line1: form.address.trim(),
  city: form.city.trim(),
  region: form.stateProvinceRegion.trim(),
  postalCode: form.postalCode.trim(),
  countryCode: form.countryCode.trim().toUpperCase(),
  latitude: form.latitude,
  longitude: form.longitude,
})
const ownerOrgId = (location: AccessibleLocation) => location.ownerOrgId || location.owner_org_id || location.companyOrgId || location.company_org_id || ''

const hasGeofence = (location: AccessibleLocation) => {
  const shapedLocation = location as AccessibleLocation & { circle?: unknown; polygon?: unknown }
  return Boolean(
    shapedLocation.circle
    || shapedLocation.polygon
    || shapedLocation.fence
    || shapedLocation.fence_id
    || shapedLocation.fenceId
    || location.metadata?.geofence
    || location.metadata?.fence
  )
}

const geofenceSummary = (location: AccessibleLocation, t: TFunc) => {
  const shapedLocation = location as AccessibleLocation & {
    fence?: { type?: string; circle?: { radius?: number | null } | null; polygon?: unknown[] | null }
    fenceType?: string | null
    fence_type?: string | null
    radiusMeters?: number | null
    radius_meters?: number | null
    polygon?: unknown[] | null
  }
  const type = shapedLocation.fence?.type || shapedLocation.fenceType || shapedLocation.fence_type
  const radius = shapedLocation.fence?.circle?.radius ?? shapedLocation.radiusMeters ?? shapedLocation.radius_meters
  if (type === 'circle' && radius) return t('Circle fence, {radius}m radius', { radius: Number(radius).toFixed(0) })
  if (type === 'polygon') {
    const points = shapedLocation.fence?.polygon || shapedLocation.polygon || []
    return Array.isArray(points) && points.length
      ? t('Polygon fence, {count} points', { count: points.length })
      : t('Polygon fence')
  }
  return hasGeofence(location) ? t('Saved geofence configured') : t('Needs fence')
}

const isLocationTrackingEnabled = (location: AccessibleLocation) => {
  const raw = location as AccessibleLocation & {
    enableActiveTracking?: unknown
    enable_active_tracking?: unknown
    active_tracking?: unknown
    metadata?: Record<string, unknown>
  }

  return Boolean(
    raw.activeTracking
    || raw.trackingEnabled
    || raw.enableActiveTracking
    || raw.enable_active_tracking
    || raw.active_tracking
    || raw.metadata?.activeTracking
    || raw.metadata?.trackingEnabled
    || raw.metadata?.enableActiveTracking
    || raw.metadata?.enable_active_tracking
  )
}

const setupHint = (location: AccessibleLocation, t: TFunc) => {
  const geofenceConfigured = hasGeofence(location)
  const trackingEnabled = isLocationTrackingEnabled(location)

  if (geofenceConfigured && !trackingEnabled) {
    return t('Geofence is configured. Enable live worker tracking in Location Edit to show breadcrumbs/live map.')
  }

  if (!geofenceConfigured && trackingEnabled) {
    return t('Live tracking is enabled, but this location still needs a geofence.')
  }

  if (!geofenceConfigured && !trackingEnabled) {
    return t('Configure a geofence and enable live worker tracking before using breadcrumbs/live map.')
  }

  return t('Geofence and live worker tracking are ready for assigned shifts.')
}

const today = () => new Date().toISOString().slice(0, 10)
const weekAgo = () => {
  const date = new Date()
  date.setDate(date.getDate() - 6)
  return date.toISOString().slice(0, 10)
}

const statusLabel = (value?: string | null) => (value || 'punched_out').replace(/_/g, ' ')
const titleCase = (value?: string | null) => (value ? value.replace(/_/g, ' ') : '-').replace(/\b\w/g, (letter) => letter.toUpperCase())
const statusClass = (status?: string | null) => `status-pill status-pill--${String(status || '').replace(/[^a-z0-9_-]/gi, '_')}`
const locationStatusTone = (status?: string | null, active?: boolean | null): AdminStatusTone => {
  const normalized = String(status || (active === false ? 'inactive' : 'active')).toLowerCase()
  if (['active', 'open', 'configured'].includes(normalized)) return 'success'
  if (['inactive', 'archived', 'disabled'].includes(normalized)) return 'inactive'
  if (['pending', 'pending_deletion'].includes(normalized)) return 'pending'
  return 'neutral'
}
const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}
const onTheWayLabel = (worker: LiveWorkerLocationDto, t: (text: string, values?: Record<string, string | number>) => string) => {
  if (!worker.onTheWayAt) return ''
  const since = new Date(worker.onTheWayAt).toLocaleTimeString()
  // ETA/distance come from the backend straight-line heuristic. When they are absent
  // (no worker origin or no canonical destination coordinates) show an honest state
  // rather than fabricating an ETA on the client.
  const eta = worker.onTheWayEtaLabel || (worker.onTheWayEtaMinutes != null ? t('{minutes} min ETA', { minutes: worker.onTheWayEtaMinutes }) : '')
  return eta
    ? t('Since {time} / ETA {eta}', { time: since, eta })
    : t('Since {time} / ETA unavailable', { time: since })
}
const liveWorkerId = (worker: LiveWorkerLocationDto) => worker.userId || worker.workerProfileId || worker.globalWorkerProfileId || worker.worker_profile_id || ''
const liveWorkerName = (worker: LiveWorkerLocationDto, fallbackLabel: string) => worker.displayName || worker.workerName || worker.worker_name || worker.name || worker.email || worker.phone || fallbackLabel
const liveWorkerShiftTime = (worker: LiveWorkerLocationDto) => worker.shiftStartedAt || worker.shift_started_at || worker.startedAt || worker.started_at || ''
const liveWorkerScheduleLabel = (worker: LiveWorkerLocationDto, t: TFunc) => {
  if (worker.isLate || worker.late || worker.scheduleStatus === 'late' || worker.timeState === 'late') return t('Late')
  const status = String(worker.status || '').toLowerCase()
  if (status === 'punched_in') return t('Live')
  if (status === 'break') return t('Live / break')
  return t('Scheduled')
}
const liveWorkerPagination = (total: number, page: number, pageSize: number): PaginationMeta => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const safePage = Math.min(Math.max(1, page), totalPages)
  return {
    page: safePage,
    pageSize,
    total,
    totalPages,
    hasNextPage: safePage < totalPages,
    hasPreviousPage: safePage > 1,
  }
}
const hasLocationCapabilityFlag = (location: AccessibleLocation, keys: Array<keyof AccessibleLocation>) => (
  keys.some((key) => location[key] !== undefined)
)

const jobsRollup = (location: AccessibleLocation) => {
  const camel = location.jobsAtLocation
  const snake = location.jobs_at_location
  return {
    totalJobsCount: Number(camel?.totalJobsCount ?? snake?.total_jobs_count ?? 0),
    openJobsCount: Number(camel?.openJobsCount ?? snake?.open_jobs_count ?? 0),
    activeJobsCount: Number(camel?.activeJobsCount ?? snake?.active_jobs_count ?? 0),
    draftJobsCount: Number(camel?.draftJobsCount ?? snake?.draft_jobs_count ?? 0),
    completedJobsCount: Number(camel?.completedJobsCount ?? snake?.completed_jobs_count ?? 0),
    cancelledJobsCount: Number(camel?.cancelledJobsCount ?? snake?.cancelled_jobs_count ?? 0),
    jobs: camel?.jobs || snake?.jobs || [],
  }
}

const shiftsRollup = (location: AccessibleLocation) => {
  const camel = location.shiftsAtLocation
  const snake = location.shifts_at_location
  return {
    totalShiftsCount: Number(camel?.totalShiftsCount ?? snake?.total_shifts_count ?? 0),
    upcomingShiftsCount: Number(camel?.upcomingShiftsCount ?? snake?.upcoming_shifts_count ?? 0),
    openShiftsCount: Number(camel?.openShiftsCount ?? snake?.open_shifts_count ?? 0),
    assignedShiftsCount: Number(camel?.assignedShiftsCount ?? snake?.assigned_shifts_count ?? 0),
    inProgressShiftsCount: Number(camel?.inProgressShiftsCount ?? snake?.in_progress_shifts_count ?? 0),
    completedShiftsCount: Number(camel?.completedShiftsCount ?? snake?.completed_shifts_count ?? 0),
    cancelledShiftsCount: Number(camel?.cancelledShiftsCount ?? snake?.cancelled_shifts_count ?? 0),
    openSlotsCount: Number(camel?.openSlotsCount ?? snake?.open_slots_count ?? 0),
    upcomingWorkersNeededCount: Number(camel?.upcomingWorkersNeededCount ?? snake?.upcoming_workers_needed_count ?? 0),
    nextShiftStartsAt: camel?.nextShiftStartsAt || snake?.next_shift_starts_at || null,
    shifts: camel?.shifts || snake?.shifts || [],
  }
}

// Convert a GrantedClientLocation to the minimal AccessibleLocation shape needed by BreadcrumbTrailViewer
const grantedToAccessible = (loc: GrantedClientLocation): AccessibleLocation => ({
  id: loc.locationId,
  name: loc.name,
  location_key: loc.locationKey || undefined,
  activeTracking: true,
  trackingEnabled: true,
})

export const LocationsPanel: React.FC<{
  organizations: Organization[]
  brandingRows: OrganizationBranding[]
  organizationUnits: OrganizationUnit[]
  accessibleLocations: AccessibleLocation[]
  canManageLocations: boolean
  canCreateJobs: boolean
  canUseLiveTracking?: boolean
  relationshipAccess?: RelationshipAccessEntry[]
  currentOrgId?: string
  highlightClientOrgId?: string
  onCreateJobFromLocation: (location: AccessibleLocation) => void
  onCreateDelegatedJobFromGrantedLocation?: (entry: RelationshipAccessEntry, loc: GrantedClientLocation) => void
  onRefresh: () => Promise<void>
  suppressPageHeader?: boolean
}> = ({ organizations, brandingRows, organizationUnits, accessibleLocations, canManageLocations, canCreateJobs, canUseLiveTracking = false, relationshipAccess = [], currentOrgId, highlightClientOrgId, onCreateJobFromLocation, onCreateDelegatedJobFromGrantedLocation, onRefresh, suppressPageHeader = false }) => {
  const { t, locale } = useI18n()
  const { accessContext } = useAccessContext()
  const listScope = useAdminListState()
  // Reuse verified Admin authority; the persisted tuple is a partition, never a grant.
  const intentScope = canUseLiveTracking && listScope?.scopeKey && accessContext?.currentUser?.id && accessContext.tenantId
    ? JSON.stringify([accessContext.currentUser.id, accessContext.tenantId, currentOrgId || null]) : null
  const restoredLiveMap = () => {
    const intent = readLiveMapIntent(intentScope)
    if (!intent) return null
    const granted = relationshipAccess.flatMap(entry => entry.grantedLocations || []).find(location => location.locationId === intent.locationId)
    const location = intent.kind === 'owned' ? accessibleLocations.find(location => location.id === intent.locationId)
      : granted ? grantedToAccessible(granted) : null
    // Restore labels/coordinates only from today's authorized projection, never a saved record.
    return location ? { kind: intent.kind, location } : null
  }

  const industryLabels = useIndustryTerminology()
  const columns = useMemo(() => LOCATION_COLUMNS.map(column => ({ ...column,
    label: column.id === 'name' && industryLabels.explicitIndustry ? industryLabels.label('location') : column.label,
  })), [industryLabels.explicitIndustry, industryLabels.label])
  const compareLocations = useMemo(() => createLocationComparator(locale), [locale])
  const [selectedLocation, setSelectedLocation] = useState<AccessibleLocation | null>(null)
  const [editingLocation, setEditingLocation] = useState<AccessibleLocation | null>(null)
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [geofenceLocation, setGeofenceLocation] = useState<AccessibleLocation | null>(null)
  const [breadcrumbLocation, setBreadcrumbLocation] = useState<AccessibleLocation | null>(() => restoredLiveMap()?.kind === 'owned' ? restoredLiveMap()?.location || null : null)
  // For granted-location live map (agency viewing a client company's location)
  const [breadcrumbGrantedLocation, setBreadcrumbGrantedLocation] = useState<AccessibleLocation | null>(() => restoredLiveMap()?.kind === 'granted' ? restoredLiveMap()?.location || null : null)
  const [historyLocation, setHistoryLocation] = useState<AccessibleLocation | null>(null)
  const [saving, setSaving] = useState(false)
  const [loadingOps, setLoadingOps] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [form, setForm] = useState<LocationForm>({ name: '', address: '', city: '', stateProvinceRegion: '', postalCode: '', countryCode: '', latitude: '', longitude: '', timezone: '', ownerOrgId: '', requireShift: true, activeTracking: false })
  // Snapshot of the hydrated address so edits to any component can be detected
  // and stale resolved coordinates are never silently kept for a new address.
  const [addressBaseline, setAddressBaseline] = useState<AddressBaseline>(emptyAddressBaseline)
  const [pinConfirmed, setPinConfirmed] = useState(false)
  const [liveWorkerRows, setLiveWorkerRows] = useState<LiveWorkerLocationDto[]>([])
  const [liveWorkerPage, setLiveWorkerPage] = useState(1)
  const [liveWorkerPageSize, setLiveWorkerPageSize] = useState(10)
  const [messageRows, setMessageRows] = useState<LocationMessageRow[]>([])
  const [timesheetRows, setTimesheetRows] = useState<LocationTimesheetRow[]>([])
  const [timesheetFrom, setTimesheetFrom] = useState(weekAgo())
  const [timesheetTo, setTimesheetTo] = useState(today())
  const [messageBody, setMessageBody] = useState('')
  const [messageAudience, setMessageAudience] = useState<'location' | 'active' | 'worker'>('location')
  const [messageWorkerId, setMessageWorkerId] = useState('')
  const [punchOutTarget, setPunchOutTarget] = useState<LiveWorkerLocationDto | null>(null)

  useEffect(() => {
    const accessibleIds = new Set(accessibleLocations.map((location) => location.id))
    const grantedIds = new Set(relationshipAccess.flatMap((entry) => (entry.grantedLocations || []).map((location) => location.locationId)))
    const hasAuthoritativeRows = shouldReconcileLocationAccess(accessibleLocations.length, relationshipAccess.length)
    // Access-context refreshes briefly project empty arrays. Those transient values
    // must never own drawer/modal visibility. Reconcile only after a non-empty,
    // authoritative projection proves that a selected resource disappeared.
    if (!hasAuthoritativeRows) return
    setSelectedLocation((current) => current && accessibleIds.has(current.id) ? current : null)
    setGeofenceLocation((current) => current && accessibleIds.has(current.id) ? current : null)
    setHistoryLocation((current) => current && (accessibleIds.has(current.id) || grantedIds.has(current.id)) ? current : null)
    // Live-map authorization is reconciled by its scoped endpoint. Access arrays
    // can be partial during background context refreshes even when other rows are
    // present, so they are never allowed to dismiss an open map.
  }, [accessibleLocations, relationshipAccess, t])

  useEffect(() => {
    const intent = restoredLiveMap()
    if (!intent || breadcrumbLocation || breadcrumbGrantedLocation) return
    if (intent.kind === 'owned') setBreadcrumbLocation(intent.location)
    else setBreadcrumbGrantedLocation(intent.location)
  }, [accessibleLocations, relationshipAccess, breadcrumbLocation, breadcrumbGrantedLocation, intentScope])

  const openOwnedLiveMap = (location: AccessibleLocation) => {
    if (!intentScope) return
    writeLiveMapIntent({ version: 1, scope: intentScope, kind: 'owned', locationId: location.id })
    setBreadcrumbLocation(location)
  }
  const openGrantedLiveMap = (location: AccessibleLocation) => {
    if (!intentScope) return
    writeLiveMapIntent({ version: 1, scope: intentScope, kind: 'granted', locationId: location.id })
    setBreadcrumbGrantedLocation(location)
  }
  const closeLiveMap = (reason?: 'escape' | 'button' | 'backdrop') => {
    if (!reason) return
    writeLiveMapIntent(null)
    setBreadcrumbLocation(null)
    setBreadcrumbGrantedLocation(null)
  }
  const closeUnavailableLiveMap = (unavailableMessage: string) => {
    writeLiveMapIntent(null)
    setBreadcrumbLocation(null)
    setBreadcrumbGrantedLocation(null)
    setMessage(unavailableMessage)
  }

  useEffect(() => {
    if (!pendingGeofenceIntent) return
    if (Date.now() - pendingGeofenceIntent.at > PENDING_GEOFENCE_INTENT_TTL_MS) {
      pendingGeofenceIntent = null
      return
    }
    const created = accessibleLocations.find((location) => location.id === pendingGeofenceIntent?.locationId)
    if (!created) return
    pendingGeofenceIntent = null
    setGeofenceLocation(created)
    setMessage(t('Location created. Next, configure its punch geofence.'))
  }, [accessibleLocations, t])
  const listSource = useMemo(() => [accessibleLocations, compareLocations], [accessibleLocations, compareLocations])
  const tableDefinition = useMemo(() => locationTableDefinition(organizations, columns), [organizations, columns])
  const locationList = useSmartTable<AccessibleLocation, LocationTableFilters>(tableDefinition, {
    source: listSource,
    fetchPage: async (query) => {
      const term = String(query.search || '').trim().toLowerCase()
      const rows = accessibleLocations
        .filter((location) => {
          const active = location.status !== 'inactive' && location.active !== false
          if (query.status === 'active' && !active) return false
          if (query.status === 'inactive' && active) return false
          if (query.ownerOrgId && ownerOrgId(location) !== query.ownerOrgId) return false
          if (query.hasGeofence === 'yes' && !hasGeofence(location)) return false
          if (query.hasGeofence === 'no' && hasGeofence(location)) return false
          if (!term) return true
          return [locationName(location), locationAddress(location), location.ownerOrgName, location.owner_org_name, location.company_name]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(term))
        })
        .sort((a, b) => compareLocations(a, b, query.sortBy || 'name', query.sortDir || 'asc'))
      const page = query.page || 1
      const pageSize = query.pageSize || 25
      const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
      const safePage = Math.min(page, totalPages)
      const start = (safePage - 1) * pageSize
      return {
        items: rows.slice(start, start + pageSize),
        pagination: { page: safePage, pageSize, total: rows.length, totalPages, hasNextPage: safePage < totalPages, hasPreviousPage: safePage > 1 },
        sort: { sortBy: query.sortBy, sortDir: query.sortDir || 'asc' },
        filters: { ...query },
      }
    },
  })

  const densityPreference = locationList.density
  const columnPreference = locationList.preference
  const visibleLocations = useMemo(
    () => locationList.items,
    [locationList.items],
  )
  const { search, setSearch } = locationList
  const commandSearchRef = useRef<HTMLInputElement>(null)
  const commandFiltersRef = useRef<HTMLButtonElement>(null)
  useListCommands({ page: 'locations', enabled: true, search: commandSearchRef, filters: commandFiltersRef })
  const statusFilter = (locationList.filters.status || 'all') as 'all' | 'active' | 'inactive'
  const setStatusFilter = (value: typeof statusFilter) => locationList.setFilter('status', value === 'all' ? undefined : value)

  // Client company locations — only shown when viewer is the agency side of the relationship
  const clientLocationGroups = useMemo(() => {
    return relationshipAccess
      .filter((entry) => {
        if ((entry.grantedLocations?.length || 0) === 0) return false
        // Only the agency viewer should see granted locations under "Client Company Locations"
        // Use currentOrgId to determine viewer's side; if not available, suppress to avoid false positives
        if (currentOrgId) return entry.agencyOrg?.id === currentOrgId
        return false
      })
      .map((entry) => ({
        entry,
        locations: entry.grantedLocations || [],
      }))
  }, [relationshipAccess, currentOrgId])

  // Partner access summary — for company-side viewers, map locationId → agency relationships
  const locationPartnerAccess = useMemo(() => {
    const map: Record<string, RelationshipAccessEntry[]> = {}
    for (const entry of relationshipAccess) {
      if (!currentOrgId || entry.companyOrg?.id !== currentOrgId) continue
      for (const loc of (entry.grantedLocations || [])) {
        if (!map[loc.locationId]) map[loc.locationId] = []
        map[loc.locationId].push(entry)
      }
    }
    return map
  }, [relationshipAccess, currentOrgId])

  const clientSectionRefs = useRef<Record<string, HTMLElement | null>>({})

  useEffect(() => {
    if (!highlightClientOrgId) return
    const el = clientSectionRefs.current[highlightClientOrgId]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [highlightClientOrgId])

  const openCreate = () => {
    setEditingLocation(null)
    setError('')
    setMessage('')
    setForm({ name: '', address: '', city: '', stateProvinceRegion: '', postalCode: '', countryCode: '', latitude: '', longitude: '', timezone: '', ownerOrgId: organizations[0]?.id || '', requireShift: true, activeTracking: false })
    setAddressBaseline(emptyAddressBaseline)
    setPinConfirmed(false)
    setShowLocationModal(true)
  }

  const openEdit = (location: AccessibleLocation) => {
    setEditingLocation(location)
    setError('')
    setMessage('')
    // Hydrate from the canonical address DTO; legacy string-address rows fill
    // line1 and leave the structured components blank.
    const address = normalizeLocationAddress(location)
    const nextForm: LocationForm = {
      name: locationName(location),
      address: address.line1 || address.formattedAddress || '',
      city: address.city || '',
      stateProvinceRegion: address.region || '',
      postalCode: address.postalCode || '',
      countryCode: address.countryCode || '',
      latitude: address.latitude == null ? '' : String(address.latitude),
      longitude: address.longitude == null ? '' : String(address.longitude),
      timezone: location.timezone || '',
      ownerOrgId: ownerOrgId(location),
      requireShift: Boolean(location.requireShift ?? true),
      activeTracking: isLocationTrackingEnabled(location),
    }
    setForm(nextForm)
    setAddressBaseline(addressBaselineFromForm(nextForm))
    setPinConfirmed(false)
    setShowLocationModal(true)
  }

  const canManageLocation = (location?: AccessibleLocation | null) => {
    if (!canManageLocations || !location) return false
    if (hasLocationCapabilityFlag(location, ['canManage', 'can_manage'])) {
      return Boolean(location.canManage ?? location.can_manage)
    }
    return true
  }

  const canManageLocationTracking = (location?: AccessibleLocation | null) => {
    if (!canManageLocations || !location) return false
    if (hasLocationCapabilityFlag(location, ['canManageTracking', 'can_manage_tracking', 'canManage', 'can_manage'])) {
      return Boolean(location.canManageTracking ?? location.can_manage_tracking ?? location.canManage ?? location.can_manage)
    }
    return true
  }

  const canEditCurrentLocation = editingLocation ? canManageLocation(editingLocation) : canManageLocations
  const canEditCurrentTracking = canUseLiveTracking && (editingLocation ? canManageLocationTracking(editingLocation) : canManageLocations)

  const currentBaseline = addressBaselineFromForm(form)
  const addressComponentsDirty = Boolean(editingLocation) && (
    currentBaseline.line1 !== addressBaseline.line1
    || currentBaseline.city !== addressBaseline.city
    || currentBaseline.region !== addressBaseline.region
    || currentBaseline.postalCode !== addressBaseline.postalCode
    || currentBaseline.countryCode !== addressBaseline.countryCode
  )
  const coordinatesEdited = currentBaseline.latitude !== addressBaseline.latitude || currentBaseline.longitude !== addressBaseline.longitude
  const baselineHadCoordinates = addressBaseline.latitude !== '' && addressBaseline.longitude !== ''
  // Editing an address component makes previously resolved coordinates stale:
  // they are dropped on save unless the user re-positions them or explicitly
  // confirms the existing map pin still applies.
  const resolutionStale = addressComponentsDirty && baselineHadCoordinates && !coordinatesEdited && !pinConfirmed

  const submitLocation = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canEditCurrentLocation) {
      setError(t('You can view this location but cannot change tracking settings.'))
      return
    }
    if (!form.name.trim()) {
      setError(t('Location name is required.'))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const owner = organizations.find((org) => org.id === form.ownerOrgId)
      const parsedLatitude = form.latitude === '' ? null : Number(form.latitude)
      const parsedLongitude = form.longitude === '' ? null : Number(form.longitude)
      const coordinatesValid = Number.isFinite(parsedLatitude as number) && Number.isFinite(parsedLongitude as number)
      const latitude = resolutionStale || !coordinatesValid ? null : parsedLatitude
      const longitude = resolutionStale || !coordinatesValid ? null : parsedLongitude
      const hasAddressText = Boolean(form.address.trim())
      const hasCoordinates = latitude !== null && longitude !== null
      const displayAddress = [form.address.trim(), form.city.trim(), form.stateProvinceRegion.trim(), form.postalCode.trim(), form.countryCode.trim().toUpperCase()].filter(Boolean).join(', ') || null
      const payload = {
        company: owner?.name || '',
        name: form.name.trim(),
        ownerOrgId: form.ownerOrgId || null,
        addressLine1: form.address.trim() || null,
        displayAddress,
        city: form.city.trim() || null,
        stateProvinceRegion: form.stateProvinceRegion.trim() || null,
        postalCode: form.postalCode.trim() || null,
        countryCode: form.countryCode.trim().toUpperCase() || null,
        latitude,
        longitude,
        timezone: form.timezone.trim() || null,
        addressStatus: hasAddressText && hasCoordinates ? 'resolved' : hasAddressText ? 'address_only' : hasCoordinates ? 'coordinates_only' : 'unresolved',
        ...(pinConfirmed && hasCoordinates
          ? { addressResolutionMetadata: { manualPinConfirmed: true, resolutionSource: 'manual_pin', resolvedAt: new Date().toISOString() } }
          : coordinatesEdited && hasCoordinates
            ? { addressResolutionMetadata: { resolutionSource: 'manual', resolvedAt: new Date().toISOString() } }
            : {}),
        requireShift: form.requireShift,
        activeTracking: form.activeTracking,
      }
      let createdLocation: AccessibleLocation | null = null
      if (editingLocation?.id) {
        await adminService.updateLocationDetails(editingLocation.id, payload)
        setMessage(t('Location updated.'))
      } else {
        const response = await adminService.createLocation(payload)
        createdLocation = (response.data?.location || null) as AccessibleLocation | null
        setMessage(createdLocation
          ? t('Location created. Next, configure its punch geofence.')
          : t('Location created. Configure the geofence from the location actions.'))
        if (createdLocation?.id) pendingGeofenceIntent = { locationId: createdLocation.id, at: Date.now() }
      }
      setShowLocationModal(false)
      await onRefresh()
      if (createdLocation?.id) setGeofenceLocation(createdLocation)
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to save location.')))
    } finally {
      setSaving(false)
    }
  }

  const dependencySummary = (dependencies: Array<{ type: string; count: number }>) => (
    dependencies.map((dependency) => `${dependency.type}: ${dependency.count}`).join(', ') || t('none')
  )

  const requestLifecycleReason = (label: string) => window.prompt(`${label}\n\n${t('Reason is required.')}`)?.trim() || ''

  const runLocationLifecycle = async (label: string, action: (reason: string) => Promise<unknown>, success: string, destructive = false) => {
    const reason = requestLifecycleReason(label)
    if (!reason) return
    if (destructive && !window.confirm(label)) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await action(reason)
      setMessage(success)
      await onRefresh()
      setSelectedLocation(null)
    } catch (err) {
      setError(apiErrorMessage(err, t('System lifecycle action failed.')))
    } finally {
      setSaving(false)
    }
  }

  const deleteLocationWithImpact = async (locationId: string) => {
    const reason = requestLifecycleReason(t('Delete = remove only if this location has no historical dependencies.'))
    if (!reason) return
    setSaving(true)
    setError('')
    try {
      const impact = await getLocationDeleteImpact(locationId)
      if (!impact.canDelete) {
        throw new Error(t('Delete blocked. Dependencies: {dependencies}. Recommended action: {action}.', { dependencies: dependencySummary(impact.dependencies), action: impact.recommendedAction }))
      }
      if (!window.confirm(t('No dependencies were found. Delete this empty location?'))) return
      await deleteLocation(locationId, reason)
      setMessage(t('Location deleted.'))
      await onRefresh()
      setSelectedLocation(null)
    } catch (err) {
      setError(apiErrorMessage(err, t('Location delete failed.')))
    } finally {
      setSaving(false)
    }
  }

  const clearGeofenceWithImpact = async (locationId: string) => {
    const reason = requestLifecycleReason(t('Clear geofence = remove geofence config only if no active usage depends on it.'))
    if (!reason) return
    setSaving(true)
    setError('')
    try {
      const impact = await getLocationGeofenceDeleteImpact(locationId)
      if (!impact.canDelete) {
        throw new Error(t('Geofence clear blocked. Dependencies: {dependencies}. Recommended action: {action}.', { dependencies: dependencySummary(impact.dependencies), action: impact.recommendedAction }))
      }
      if (!window.confirm(t('No geofence dependencies were found. Clear this geofence?'))) return
      await clearLocationGeofence(locationId, reason)
      setMessage(t('Geofence cleared.'))
      await onRefresh()
    } catch (err) {
      setError(apiErrorMessage(err, t('Geofence clear failed.')))
    } finally {
      setSaving(false)
    }
  }

  const selectedOwner = selectedLocation ? findBrand(ownerOrgId(selectedLocation), organizations, brandingRows) : null
  const geofenceOwner = geofenceLocation ? findBrand(ownerOrgId(geofenceLocation), organizations, brandingRows) : null
  const linkedOuForLocation = (location: AccessibleLocation) => organizationUnits.find((unit) => unit.linked_location_id === location.id)
  const ownerOrgForLocation = (location: AccessibleLocation) => organizations.find((org) => org.id === ownerOrgId(location))
  const workerLabel = industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker')
  const liveWorkerPageMeta = useMemo(
    () => liveWorkerPagination(liveWorkerRows.length, liveWorkerPage, liveWorkerPageSize),
    [liveWorkerRows.length, liveWorkerPage, liveWorkerPageSize],
  )
  const paginatedLiveWorkers = useMemo(() => {
    const start = (liveWorkerPageMeta.page - 1) * liveWorkerPageMeta.pageSize
    return liveWorkerRows.slice(start, start + liveWorkerPageMeta.pageSize)
  }, [liveWorkerRows, liveWorkerPageMeta.page, liveWorkerPageMeta.pageSize])
  const selectedOwnerName = organizations.find((org) => org.id === locationList.filters.ownerOrgId)?.name
  const activeLocationFilterCount = [
    statusFilter !== 'all',
    Boolean(locationList.filters.ownerOrgId),
    Boolean(locationList.filters.hasGeofence),
  ].filter(Boolean).length
  const activeLocationFilters = [
    statusFilter !== 'all'
      ? { id: 'status', label: `${t('Status')}: ${t(titleCase(statusFilter))}`, onRemove: () => setStatusFilter('all') }
      : null,
    locationList.filters.ownerOrgId
      ? { id: 'owner', label: `${t('Owner')}: ${selectedOwnerName || t('Selected owner')}`, onRemove: () => locationList.setFilter('ownerOrgId', undefined) }
      : null,
    locationList.filters.hasGeofence
      ? { id: 'geofence', label: `${t('Geofence')}: ${locationList.filters.hasGeofence === 'yes' ? t('Has geofence') : t('Needs geofence')}`, onRemove: () => locationList.setFilter('hasGeofence', undefined) }
      : null,
  ].filter((filter): filter is { id: string; label: string; onRemove: () => void } => Boolean(filter))
  const clearLocationFilters = () => {
    setStatusFilter('all')
    locationList.setFilter('ownerOrgId', undefined)
    locationList.setFilter('hasGeofence', undefined)
  }

  const loadLocationOperations = async (location = selectedLocation) => {
    if (!location?.id) return
    setLoadingOps(true)
    setError('')
    try {
      const [liveWorkersResponse, messagesResponse, timesheetResponse] = await Promise.all([
        canUseLiveTracking
          ? adminService.listLiveWorkersForLocation(location.id, { includeLatestOnly: true })
          : Promise.resolve({ data: { data: [] } }),
        adminService.listLocationAdminMessages(location.id),
        adminService.getLocationTimesheets(location.id, { from: timesheetFrom, to: timesheetTo }),
      ])
      setLiveWorkerRows((liveWorkersResponse.data?.data || []) as LiveWorkerLocationDto[])
      setLiveWorkerPage(1)
      setMessageRows(messagesResponse.data?.messages || [])
      setTimesheetRows(timesheetResponse.data?.rows || [])
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to load location operations.')))
    } finally {
      setLoadingOps(false)
    }
  }

  useEffect(() => {
    if (!selectedLocation?.id) {
      setLiveWorkerRows([])
      setLiveWorkerPage(1)
      setMessageRows([])
      setTimesheetRows([])
      return
    }
    loadLocationOperations(selectedLocation)
  }, [selectedLocation?.id, timesheetFrom, timesheetTo, canUseLiveTracking])

  useEffect(() => {
    if (messageAudience !== 'worker' || !messageWorkerId) return
    if (!liveWorkerRows.some((worker) => liveWorkerId(worker) === messageWorkerId)) setMessageWorkerId('')
  }, [liveWorkerRows, messageAudience, messageWorkerId])

  const sendLocationMessage = async () => {
    if (!selectedLocation?.id || !messageBody.trim()) return
    if (messageAudience === 'worker' && !messageWorkerId) {
      setError(t('Select a worker before sending a direct location message.'))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const response = await adminService.sendAdminMessage({
        locationId: selectedLocation.id,
        audience: messageAudience,
        workerId: messageAudience === 'worker' ? messageWorkerId : undefined,
        message: messageBody.trim(),
      })
      setMessage(t('{count} messages sent.', { count: response.data?.sentCount || 0 }))
      setMessageBody('')
      await loadLocationOperations(selectedLocation)
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to send location message.')))
    } finally {
      setSaving(false)
    }
  }

  const punchOutWorker = async () => {
    const workerId = punchOutTarget ? liveWorkerId(punchOutTarget) : ''
    if (!selectedLocation?.id || !workerId) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const key = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`
      const response = await adminService.punchOutWorker(selectedLocation.id, workerId, 'Manual Admin Workspace punch-out', key)
      const replayed = response.data?.idempotentReplay || response.data?.alreadyCompleted
      setMessage(t('Worker punched out.'))
      if (replayed) setMessage(t('Worker punch status is already current.'))
      setPunchOutTarget(null)
      await loadLocationOperations(selectedLocation)
    } catch (err) {
      setError(apiErrorMessage(err, t('You do not have permission to punch out this worker for this location or agency.')))
    } finally {
      setSaving(false)
    }
  }


  const addLocationLabel = industryLabels.explicitIndustry ? `Add ${industryLabels.label('location').toLowerCase()}` : t('Add location')
  const addLocationButton = canManageLocations ? (
    <button type="button" className="btn btn-primary" data-testid="add-location-button" aria-label={addLocationLabel} onClick={openCreate}>
      {addLocationLabel}
    </button>
  ) : null

  return (
    <AdminPage width="full" testId="admin-locations-page">
      {!suppressPageHeader ? (
        <AdminPageHeader
          title={industryLabels.explicitIndustry ? `${industryLabels.label('locations')} & Geofences` : t('Locations & Geofences')}
          description={industryLabels.explicitIndustry ? `Manage access-scoped ${industryLabels.label('locations').toLowerCase()}, geofences, live maps, and location operations.` : t('Manage access-scoped locations, geofences, live maps, and location operations.')}
          status={<AdminStatusBadge tone="info">{t('{count} visible', { count: locationList.pagination.total })}</AdminStatusBadge>}
          primaryAction={addLocationButton}
        />
      ) : (
        <div className="admin-page-actions-row">
          <AdminStatusBadge tone="info">{t('{count} visible', { count: locationList.pagination.total })}</AdminStatusBadge>
          {addLocationButton ? <div className="admin-page-actions-row__primary">{addLocationButton}</div> : null}
        </div>
      )}

      <AdminFilterBar
        advancedButtonRef={commandFiltersRef}
        resultCount={locationList.pagination.total}
        activeFilterCount={activeLocationFilterCount}
        onClear={clearLocationFilters}
        ariaLabel={t('Location filters')}
        advanced={(
          <>
            <label>{t('Owner')}<select className="input" value={String(locationList.filters.ownerOrgId || '')} onChange={(event) => locationList.setFilter('ownerOrgId', event.target.value || undefined)}>
              <option value="">{t('All owners')}</option>
              {organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
            </select></label>
            <label>{t('Geofence')}<select className="input" value={String(locationList.filters.hasGeofence || '')} onChange={(event) => locationList.setFilter('hasGeofence', event.target.value || undefined)}>
              <option value="">{t('Any geofence')}</option>
              <option value="yes">{t('Has geofence')}</option>
              <option value="no">{t('Needs geofence')}</option>
            </select></label>
          </>
        )}
      >
        <label>{t('Search')}<input ref={commandSearchRef} className="input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={industryLabels.explicitIndustry ? `${industryLabels.label('location')}, owner, address` : t('Location, owner, address')} /></label>
        <label>{t('Status')}<select className="input" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}>
          <option value="all">{t('All statuses')}</option>
          <option value="active">{t('Active')}</option>
          <option value="inactive">{t('Inactive')}</option>
        </select></label>
      </AdminFilterBar>
      <AdminActiveFilterChips filters={activeLocationFilters} onClear={clearLocationFilters} />
      <TableSortControls table={locationList} />
      <TableDisplayControls table={locationList} clearFilters={clearLocationFilters} />
      {!canManageLocations ? <p className="helper">{industryLabels.explicitIndustry ? `${industryLabels.label('location')} and geofence edits require location management permission. Visible ${industryLabels.label('locations').toLowerCase()} are still scoped by access context.` : t('Location and geofence edits require location management permission. Visible locations are still scoped by access context.')}</p> : null}
      {message ? <p className="success-text">{message}</p> : null}
      <ErrorBanner message={error} />

      <AdminTableContainer
        label={industryLabels.explicitIndustry ? `${industryLabels.label('locations')} table` : t('Locations table')}
        density={densityPreference.density}
        isEmpty={visibleLocations.length === 0}
        pagination={<PaginationControls pagination={locationList.pagination} onPageChange={locationList.setPage} onPageSizeChange={locationList.setPageSize} />}
      >
        <table className="workspace-table workspace-table--locations">
          <thead><tr>{columns.filter(column => columnPreference.isVisible(column.id)).map(column => <th
            key={column.id} scope="col" data-column-id={column.id}
            aria-sort={locationList.sortBy === column.id ? (locationList.sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
          >{column.id === 'name' || column.id === 'status'
              ? <SortableHeader label={t(column.label)} sortKey={column.id} activeSortBy={locationList.sortBy} sortDir={locationList.sortDir} onSort={locationList.setSort} />
              : t(column.label)}</th>)}</tr></thead>
          <tbody>
            {visibleLocations.map((location) => {
              const owner = findBrand(ownerOrgId(location), organizations, brandingRows)
              const linkedOu = linkedOuForLocation(location)
              const locationManageAllowed = canManageLocation(location)
              const partners = locationPartnerAccess[location.id] || []
              const rowActions: AdminRowAction[] = [
                { id: 'view', label: t('View details'), onSelect: () => setSelectedLocation(location) },
                { id: 'edit', label: t('Edit'), disabled: !locationManageAllowed, disabledReason: t('You can view this location, but editing requires location management access.'), onSelect: () => openEdit(location) },
                { id: 'geofence', label: locationManageAllowed ? t('Configure geofence') : t('View geofence'), onSelect: () => setGeofenceLocation(location) },
                { id: 'create-job', label: industryLabels.explicitIndustry ? `Create ${industryLabels.label('job').toLowerCase()} here` : t('Create job here'), disabled: !canCreateJobs, disabledReason: t('Creating jobs requires job creation access.'), onSelect: () => onCreateJobFromLocation(location) },
                { id: 'disable', label: t('Disable'), disabled: saving, onSelect: () => runLocationLifecycle(t('Disable = stop future use of this location while keeping history.'), (reason) => disableLocation(location.id, reason), t('Location disabled.')) },
                { id: 'archive', label: t('Archive'), disabled: saving, onSelect: () => runLocationLifecycle(t('Archive = hide this location from normal operations but keep history.'), (reason) => archiveLocation(location.id, reason), t('Location archived.')) },
                { id: 'restore', label: t('Restore'), disabled: saving, onSelect: () => runLocationLifecycle(t('Restore this location to operational use.'), (reason) => restoreLocation(location.id, reason), t('Location restored.')) },
                { id: 'delete', label: t('Delete'), disabled: saving, destructive: true, onSelect: () => deleteLocationWithImpact(location.id) },
                { id: 'purge-demo', label: t('Purge demo data'), disabled: saving, destructive: true, onSelect: () => runLocationLifecycle(t('Purge demo data = irreversible cleanup for demo/test location records only.'), (reason) => purgeDemoLocation(location.id, reason), t('Demo location data purged.'), true) },
                { id: 'disable-geofence', label: t('Disable geofence'), disabled: saving || !hasGeofence(location), disabledReason: t('This location does not have a geofence to disable.'), onSelect: () => runLocationLifecycle(t('Disable geofence = stop geofence validation for future use.'), (reason) => disableLocationGeofence(location.id, reason), t('Geofence disabled.')) },
                { id: 'clear-geofence', label: t('Clear geofence'), disabled: saving || !hasGeofence(location), disabledReason: t('This location does not have a geofence to clear.'), destructive: true, onSelect: () => clearGeofenceWithImpact(location.id) },
                { id: 'restore-geofence', label: t('Restore geofence'), disabled: saving || !hasGeofence(location), disabledReason: t('This location does not have a geofence to restore.'), onSelect: () => runLocationLifecycle(t('Restore geofence validation for this location.'), (reason) => restoreLocationGeofence(location.id, reason), t('Geofence restored.')) },
              ]
              return (
                <tr key={location.id}>
                  <td data-column-id="name"><button type="button" className="link-button" onClick={() => setSelectedLocation(location)}>{t(locationName(location))}</button></td>
                  {columnPreference.isVisible('owner') ? <td data-column-id="owner" data-label={t('Owner')}>{owner.organization ? <BrandBadge {...owner} label={formatOrgType(owner.organization.org_type)} /> : <span className="admin-disabled-callout">{t('No owner org')}</span>}</td> : null}
                  {columnPreference.isVisible('linked-ou') ? <td data-column-id="linked-ou" data-label={t('Linked OU')}>{linkedOu ? <span className="admin-chip">{linkedOu.name}</span> : <span className="admin-disabled-callout">{t('No linked OU visible')}</span>}</td> : null}
                  {columnPreference.isVisible('address') ? <td data-column-id="address" data-label={t('Address')}>{locationAddress(location) || '-'}</td> : null}
                  {columnPreference.isVisible('geofence') ? <td data-column-id="geofence" data-label={t('Geofence')}><span className="admin-chip">{geofenceSummary(location, t)}</span></td> : null}
                  <td data-column-id="status" data-label={t('Status')}><AdminStatusBadge tone={locationStatusTone(location.status, location.active)}>{t(titleCase(location.status || (location.active === false ? 'inactive' : 'active')))}</AdminStatusBadge></td>
                  {columnPreference.isVisible('partner-access') ? <td data-column-id="partner-access" data-label={t('Partner Access')}>
                    {partners.length > 0 ? (
                      <div className="stack" style={{ gap: '0.25rem' }}>
                        {partners.map((partnerEntry) => {
                          const partnerLoc = (partnerEntry.grantedLocations || []).find((l) => l.locationId === location.id)
                          const agencyName = partnerEntry.agencyOrg?.name || t('Agency')
                          const caps = [
                            partnerLoc?.canView && t('View'),
                            partnerLoc?.canStaff && t(industryLabels.explicitIndustry ? industryLabels.label('workers') : 'Staff'),
                            partnerLoc?.canSchedule && t('Schedule'),
                            partnerLoc?.canCreateJobs && t('Create Jobs'),
                            partnerLoc?.canCreateShifts && t('Create Shifts'),
                          ].filter(Boolean)
                          return (
                            <span key={partnerEntry.relationshipId} className="admin-chip" style={{ fontSize: '0.75rem' }}>
                              {agencyName}{caps.length > 0 ? `: ${caps.join(' / ')}` : ''}
                            </span>
                          )
                        })}
                      </div>
                    ) : <span className="admin-disabled-callout" style={{ fontSize: '0.75rem' }}>{t('None')}</span>}
                  </td> : null}
                  <td data-column-id="actions" data-label={t('Actions')}>
                    <AdminRowActionMenu recordName={locationName(location)} actions={rowActions} />
                  </td>
                </tr>
              )
            })}
            {visibleLocations.length === 0 ? (
              <tr><td colSpan={columnPreference.visibleCount}>
                {accessibleLocations.length === 0 ? (
                  <EmptyState
                    title={industryLabels.explicitIndustry ? `No ${industryLabels.label('locations').toLowerCase()} yet` : t('No locations yet')}
                    description={t('Add your first workplace, job site, venue, or service location and configure its punch geofence.')}
                    action={canManageLocations ? (
                      <button type="button" className="btn btn-primary" data-testid="empty-add-location-button" onClick={openCreate}>
                        {addLocationLabel}
                      </button>
                    ) : undefined}
                  />
                ) : (
                  <EmptyState title={industryLabels.explicitIndustry ? `No ${industryLabels.label('locations').toLowerCase()} found` : t('No locations found')} description={industryLabels.explicitIndustry ? `Only ${industryLabels.label('locations').toLowerCase()} returned by the access context are shown here.` : t('Only locations returned by the access context are shown here.')} variant="filtered" />
                )}
              </td></tr>
            ) : null}
          </tbody>
        </table>
      </AdminTableContainer>

      <FormModal title={editingLocation ? (industryLabels.explicitIndustry ? `Edit ${industryLabels.label('location').toLowerCase()}` : t('Edit location')) : addLocationLabel} isOpen={showLocationModal} onClose={() => setShowLocationModal(false)} size="lg">
        <form className="stack" onSubmit={submitLocation}>
          <ErrorBanner message={error} testId="location-form-error" />
          <div className="admin-form-grid">
            <label>{t('Name')}<input className="input" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} disabled={!canEditCurrentLocation} /></label>
            <label>{t('Owner organization')}<select className="input" value={form.ownerOrgId} onChange={(event) => setForm({ ...form, ownerOrgId: event.target.value })} disabled={!canEditCurrentLocation}>{organizations.map((org) => <option key={org.id} value={org.id}>{org.name} ({formatOrgType(org.org_type)})</option>)}</select></label>
            <div className="admin-form-wide"><h3>{t('Worksite address')}</h3><p className="helper">{t('Used for job matching, maps, directions, and worker travel.')}</p></div>
            <label className="admin-form-wide">{t('Address line 1 / display address')}<input className="input" value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} disabled={!canEditCurrentLocation} /></label>
            <label>{t('City')}<input className="input" value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} disabled={!canEditCurrentLocation} /></label>
            <label>{t('State / province / region')}<input className="input" value={form.stateProvinceRegion} onChange={(event) => setForm({ ...form, stateProvinceRegion: event.target.value })} disabled={!canEditCurrentLocation} /></label>
            <label>{t('Postal code')}<input className="input" value={form.postalCode} onChange={(event) => setForm({ ...form, postalCode: event.target.value })} disabled={!canEditCurrentLocation} /></label>
            <label>{t('Country code')}<input className="input" value={form.countryCode} maxLength={2} onChange={(event) => setForm({ ...form, countryCode: event.target.value.toUpperCase() })} disabled={!canEditCurrentLocation} /></label>
            <label>{t('Latitude')}<input className="input" type="number" step="any" value={form.latitude} onChange={(event) => setForm({ ...form, latitude: event.target.value })} disabled={!canEditCurrentLocation} /></label>
            <label>{t('Longitude')}<input className="input" type="number" step="any" value={form.longitude} onChange={(event) => setForm({ ...form, longitude: event.target.value })} disabled={!canEditCurrentLocation} /></label>
            <label className="admin-form-wide">{t('Timezone')}<input className="input" placeholder="America/Jamaica" value={form.timezone} onChange={(event) => setForm({ ...form, timezone: event.target.value })} disabled={!canEditCurrentLocation} /></label>
            {addressComponentsDirty && baselineHadCoordinates && !coordinatesEdited ? (
              <div className="admin-form-wide">
                <p className="admin-disabled-callout" data-testid="address-resolution-stale">
                  {t('The address changed, so the saved coordinates may no longer match. Saving will clear them until the address is re-resolved, unless you confirm the map pin below.')}
                </p>
                <label className="admin-check-row">
                  <input type="checkbox" checked={pinConfirmed} onChange={(event) => setPinConfirmed(event.target.checked)} disabled={!canEditCurrentLocation} data-testid="confirm-map-pin" />
                  {' '}{t('Keep the current coordinates - the map pin was positioned manually and still applies.')}
                </label>
              </div>
            ) : null}
            <label className="admin-check-row"><input type="checkbox" checked={form.requireShift} onChange={(event) => setForm({ ...form, requireShift: event.target.checked })} disabled={!canEditCurrentLocation} /> {t('Require scheduled shift upload')}</label>
            <label className="admin-check-row"><input type="checkbox" checked={form.activeTracking} onChange={(event) => setForm({ ...form, activeTracking: event.target.checked })} disabled={!canEditCurrentTracking} /> {t('Enable live worker tracking for assigned shifts')}</label>
          </div>
          <p className="helper">{t('When an owner organization is set, the backend creates or preserves a linked location OU under that owner organization.')}</p>
          {!form.ownerOrgId ? <p className="admin-disabled-callout">{t('Owner organization is missing. Linked OU creation cannot happen without an owner organization.')}</p> : null}
          {editingLocation && !linkedOuForLocation(editingLocation) ? <p className="admin-disabled-callout">{t('No linked OU is visible for this location. OU-scoped inherited access may not reach this site until the link exists and is visible.')}</p> : null}
          {!canEditCurrentTracking ? <p className="helper">{t('You can view this location but cannot change tracking settings.')}</p> : null}
          <button type="submit" className="btn btn-primary" disabled={!canEditCurrentLocation || saving}>{saving ? t('Saving...') : (industryLabels.explicitIndustry ? `Save ${industryLabels.label('location').toLowerCase()}` : t('Save location'))}</button>
        </form>
      </FormModal>

      <DetailDrawer
        title={selectedLocation ? t(locationName(selectedLocation)) : t('Location')}
        subtitle={selectedLocation ? locationAddress(selectedLocation) || t('No address provided') : undefined}
        isOpen={Boolean(selectedLocation)}
        onClose={() => setSelectedLocation(null)}
        actions={selectedLocation ? (
          <>
            <button type="button" className="btn btn-secondary" disabled={!canManageLocation(selectedLocation)} onClick={() => openEdit(selectedLocation)}>{t('Edit')}</button>
            <button type="button" className="btn btn-secondary" disabled={!canCreateJobs} onClick={() => onCreateJobFromLocation(selectedLocation)}>{industryLabels.explicitIndustry ? `Create ${industryLabels.label('job').toLowerCase()} here` : t('Create job here')}</button>
            <button type="button" className="btn btn-secondary" disabled={!canUseLiveTracking} onClick={() => openOwnedLiveMap(selectedLocation)}>{canUseLiveTracking ? t('View live map') : `🔒 ${t('Contact Sales')}`}</button>
            <button type="button" className="btn btn-secondary" onClick={() => setHistoryLocation(selectedLocation)}>{t('View shift history')}</button>
            <button type="button" className="btn btn-primary" onClick={() => setGeofenceLocation(selectedLocation)}>{canManageLocation(selectedLocation) ? t('Configure geofence') : t('View geofence')}</button>
          </>
        ) : null}
      >
            {selectedLocation ? (
          <div className="stack">
            {(() => {
              const jobInventory = jobsRollup(selectedLocation)
              const shiftInventory = shiftsRollup(selectedLocation)
              return (
                <section className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
                  <div className="admin-panel-toolbar">
                    <div>
                      <h3 style={{ margin: 0 }}>{t('Operational inventory')}</h3>
                      <p className="helper">{industryLabels.explicitIndustry ? `Tenant-scoped ${industryLabels.label('jobs').toLowerCase()} and ${industryLabels.label('shifts').toLowerCase()} tied directly to this ${industryLabels.label('location').toLowerCase()}.` : t('Tenant-scoped jobs and shifts tied directly to this location.')}</p>
                    </div>
                    <div className="inline-group">
                      <span className="admin-chip">{t('Next shift: {value}', { value: formatDateTime(shiftInventory.nextShiftStartsAt) })}</span>
                      <button type="button" className="btn btn-secondary" disabled={!canCreateJobs} onClick={() => onCreateJobFromLocation(selectedLocation)}>{industryLabels.explicitIndustry ? `Create ${industryLabels.label('job').toLowerCase()} here` : t('Create job here')}</button>
                    </div>
                  </div>
                  <div className="admin-summary-grid">
                    <article className="panel stat-card">
                      <span>{industryLabels.explicitIndustry ? industryLabels.label('jobs') : t('Jobs')}</span>
                      <strong>{jobInventory.totalJobsCount}</strong>
                      <span className="helper">{t('{open} open / {active} active', { open: jobInventory.openJobsCount, active: jobInventory.activeJobsCount })}</span>
                    </article>
                    <article className="panel stat-card">
                      <span>{industryLabels.explicitIndustry ? industryLabels.label('shifts') : t('Shifts')}</span>
                      <strong>{shiftInventory.totalShiftsCount}</strong>
                      <span className="helper">{t('{count} upcoming', { count: shiftInventory.upcomingShiftsCount })}</span>
                    </article>
                    <article className="panel stat-card">
                      <span>{t('Open slots')}</span>
                      <strong>{shiftInventory.openSlotsCount}</strong>
                      <span className="helper">{industryLabels.explicitIndustry ? `${shiftInventory.upcomingWorkersNeededCount} upcoming ${industryLabels.label('workers').toLowerCase()} needed` : t('{count} upcoming workers needed', { count: shiftInventory.upcomingWorkersNeededCount })}</span>
                    </article>
                    <article className="panel stat-card">
                      <span>{t('In progress')}</span>
                      <strong>{shiftInventory.inProgressShiftsCount}</strong>
                      <span className="helper">{t('{completed} completed / {cancelled} cancelled', { completed: shiftInventory.completedShiftsCount, cancelled: shiftInventory.cancelledShiftsCount })}</span>
                    </article>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="workspace-table">
                      <thead><tr><th>{industryLabels.explicitIndustry ? industryLabels.label('job') : t('Job')}</th><th>{t('Status')}</th><th>{t('Company')}</th><th>{t('Agency')}</th></tr></thead>
                      <tbody>
                        {jobInventory.jobs.map((job) => (
                          <tr key={job.jobId}>
                            <td>{job.title || t('Untitled job')}</td>
                            <td><span className={statusClass(job.status)}>{t(titleCase(job.status))}</span></td>
                            <td>{job.companyOrgName || '-'}</td>
                            <td>{job.managingAgencyOrgName || '-'}</td>
                          </tr>
                        ))}
                        {jobInventory.jobs.length === 0 ? <tr><td colSpan={4} className="helper">{industryLabels.explicitIndustry ? `No ${industryLabels.label('jobs').toLowerCase()} are tied to this ${industryLabels.label('location').toLowerCase()} yet.` : t('No jobs are tied to this location yet.')}</td></tr> : null}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="workspace-table">
                      <thead><tr><th>{t('Upcoming / recent shift')}</th><th>{t('Status')}</th><th>{t('Needed')}</th><th>{t('Assigned')}</th><th>{t('Open slots')}</th></tr></thead>
                      <tbody>
                        {shiftInventory.shifts.map((shift) => (
                          <tr key={shift.jobShiftId}>
                            <td>{shift.jobTitle || t('Untitled job')}<div className="helper">{formatDateTime(shift.startsAt)} - {formatDateTime(shift.endsAt)}</div></td>
                            <td><span className={statusClass(shift.status)}>{t(titleCase(shift.status))}</span></td>
                            <td>{shift.workersNeeded ?? 0}</td>
                            <td>{shift.assignmentCount ?? 0}</td>
                            <td>{shift.openSlotCount ?? 0}</td>
                          </tr>
                        ))}
                        {shiftInventory.shifts.length === 0 ? <tr><td colSpan={5} className="helper">{t('No shifts are tied to this location yet.')}</td></tr> : null}
                      </tbody>
                    </table>
                  </div>
                </section>
              )
            })()}
            {selectedOwner ? <BrandBadge {...selectedOwner} label={t('Owner org branding')} emphasized /> : null}
            {!ownerOrgForLocation(selectedLocation) ? <p className="admin-disabled-callout">{t('This location has no owner organization visible in your current access context.')}</p> : null}
            {linkedOuForLocation(selectedLocation) ? (
              <p><strong>{t('Linked OU')}</strong><br />{linkedOuForLocation(selectedLocation)?.name}</p>
            ) : (
              <p className="admin-disabled-callout">{t('No linked OU is visible for this location. OU-scoped access may not inherit to this site.')}</p>
            )}
            <p><strong>{t('Overview')}</strong><br />{t(locationName(selectedLocation))}</p>
            <p><strong>{t('Address')}</strong><br />{locationAddress(selectedLocation) || '-'}</p>
            <p>
              <strong>{t('Setup status')}</strong><br />
              <span className="admin-chip">{t('Geofence')}: {hasGeofence(selectedLocation) ? t('Configured') : t('Missing')}</span>{' '}
              <span className="admin-chip">{t('Live tracking')}: {isLocationTrackingEnabled(selectedLocation) ? t('Enabled') : t('Disabled')}</span>
            </p>
            <p className="helper">{setupHint(selectedLocation, t)}</p>
            <p><strong>{t('Geofence summary')}</strong><br />{geofenceSummary(selectedLocation, t)}</p>
            <p><strong>{t('Breadcrumb/location tracking')}</strong><br />{isLocationTrackingEnabled(selectedLocation) ? t('Enabled for assigned shifts at this location.') : t('Disabled for this location.')}</p>
            <section className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <div className="admin-panel-toolbar">
                <div>
                  <h3 style={{ margin: 0 }}>{t('Live Map / Breadcrumbs')}</h3>
                  <p className="helper">{t('Location trails are available only while tracking is enabled and workers are on assigned shifts.')}</p>
                </div>
                <button type="button" className="btn btn-primary" disabled={!canUseLiveTracking} onClick={() => openOwnedLiveMap(selectedLocation)}>{canUseLiveTracking ? t('View live map') : `🔒 ${t('Contact Sales')}`}</button>
              </div>
              <p className="helper">{t("Access is limited to authorized supervisors/admins for the worker's assigned location or organization.")}</p>
              {isLocationTrackingEnabled(selectedLocation) ? null : <p className="helper">{t('Tracking is currently disabled for this location. Enable it in Location Edit; the geofence mapper only manages boundaries.')}</p>}
            </section>

            <section className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <div className="admin-panel-toolbar">
                <div>
                  <h3 style={{ margin: 0 }}>{t('Shift operational history')}</h3>
                  <p className="helper">{t('Review completed and current shifts, punches, breaks, tracking segments, and historical GPS trails.')}</p>
                </div>
                <button type="button" className="btn btn-secondary" onClick={() => setHistoryLocation(selectedLocation)}>{t('View shift history')}</button>
              </div>
            </section>

            <section className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <div className="admin-panel-toolbar">
                <div>
                  <h3 style={{ margin: 0 }}>{industryLabels.explicitIndustry ? `Live / scheduled ${industryLabels.label('workers').toLowerCase()}` : t('Live / scheduled workers')}</h3>
                  <p className="helper">{t('Shows only workers returned by the live map contract: punched-in, on-break, on-the-way, late, or currently scheduled for this location.')}</p>
                </div>
                <button type="button" className="btn btn-secondary" disabled={loadingOps} onClick={() => loadLocationOperations(selectedLocation)}>{t('Refresh')}</button>
              </div>
              {loadingOps ? <LoadingState label={t('Loading location operations...')} /> : null}
              <div style={{ overflowX: 'auto' }}>
                <table className="workspace-table">
                  <thead><tr><th>{workerLabel}</th><th>{t('Status')}</th><th>{t('Schedule')}</th><th>{t('On the way')}</th><th>{industryLabels.explicitIndustry ? industryLabels.label('shift') : t('Shift')}</th><th>{t('Actions')}</th></tr></thead>
                  <tbody>
                    {paginatedLiveWorkers.map((worker) => (
                      <tr key={liveWorkerId(worker) || worker.workerProfileId}>
                        <td>
                          {liveWorkerName(worker, workerLabel)}
                          {[worker.email, worker.phone].filter(Boolean).length ? (
                            <div className="helper">{[worker.email, worker.phone].filter(Boolean).join(' | ')}</div>
                          ) : null}
                        </td>
                        <td><span className="status-pill">{t(statusLabel(worker.status))}</span></td>
                        <td><span className="admin-chip">{liveWorkerScheduleLabel(worker, t)}</span></td>
                        <td>{worker.onTheWayAt ? <span className="admin-chip">{onTheWayLabel(worker, t)}</span> : <span className="helper">{t('Not marked')}</span>}</td>
                        <td>{formatDateTime(liveWorkerShiftTime(worker))}</td>
                        <td><button type="button" className="btn btn-ghost" disabled={saving || !['punched_in', 'break'].includes(String(worker.status || '').toLowerCase()) || !liveWorkerId(worker)} onClick={() => setPunchOutTarget(worker)}>{t('Punch out')}</button></td>
                      </tr>
                    ))}
                    {liveWorkerRows.length === 0 ? <tr><td colSpan={6} className="helper">{industryLabels.explicitIndustry ? `No live or scheduled ${industryLabels.label('workers').toLowerCase()} are available for this ${industryLabels.label('location').toLowerCase()}.` : t('No live or scheduled workers are available for this location.')}</td></tr> : null}
                  </tbody>
                </table>
              </div>
              <PaginationControls
                pagination={liveWorkerPageMeta}
                onPageChange={setLiveWorkerPage}
                onPageSizeChange={(pageSize) => {
                  setLiveWorkerPageSize(pageSize)
                  setLiveWorkerPage(1)
                }}
              />
            </section>

            <section className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <div className="admin-panel-toolbar">
                <h3 style={{ margin: 0 }}>{t('Location messaging')}</h3>
                <button type="button" className="btn btn-secondary" disabled={loadingOps} onClick={() => loadLocationOperations(selectedLocation)}>{t('Refresh messages')}</button>
              </div>
              <div className="admin-form-grid">
                <label>{t('Audience')}<select className="input" value={messageAudience} onChange={(event) => setMessageAudience(event.target.value as typeof messageAudience)}>
                  <option value="location">{industryLabels.explicitIndustry ? `All ${industryLabels.label('location').toLowerCase()} ${industryLabels.label('workers').toLowerCase()}` : t('All location workers')}</option>
                  <option value="active">{industryLabels.explicitIndustry ? `Currently active ${industryLabels.label('workers').toLowerCase()}` : t('Currently active workers')}</option>
                  <option value="worker">{industryLabels.explicitIndustry ? `Selected ${industryLabels.label('worker').toLowerCase()}` : t('Selected worker')}</option>
                </select></label>
                {messageAudience === 'worker' ? (
                  <label>{workerLabel}<select className="input" value={messageWorkerId} onChange={(event) => setMessageWorkerId(event.target.value)}>
                    <option value="">{industryLabels.explicitIndustry ? `Select ${industryLabels.label('worker').toLowerCase()}` : t('Select worker')}</option>
                    {liveWorkerRows.map((worker) => {
                      const workerId = liveWorkerId(worker)
                      return workerId ? <option key={workerId} value={workerId}>{liveWorkerName(worker, workerLabel)}</option> : null
                    })}
                  </select></label>
                ) : null}
                <label className="admin-form-wide">{t('Message')}<textarea className="input" rows={3} value={messageBody} onChange={(event) => setMessageBody(event.target.value)} /></label>
              </div>
              <button type="button" className="btn btn-primary" disabled={saving || !messageBody.trim() || (messageAudience === 'worker' && !messageWorkerId)} onClick={sendLocationMessage}>{t('Send message')}</button>
              <div style={{ overflowX: 'auto' }}>
                <table className="workspace-table">
                  <thead><tr><th>{t('Sent')}</th><th>{industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker')}</th><th>{t('Message')}</th><th>{t('Delivery')}</th></tr></thead>
                  <tbody>
                    {messageRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.sentAt || '-'}</td>
                        <td>{row.workerEmail || row.workerPhone || '-'}</td>
                        <td>{row.body}</td>
                        <td>{row.deliveryState || '-'}</td>
                      </tr>
                    ))}
                    {messageRows.length === 0 ? <tr><td colSpan={4} className="helper">{t('No location messages found.')}</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <div className="admin-panel-toolbar">
                <h3 style={{ margin: 0 }}>{t('Location timesheets')}</h3>
                <div className="inline-group">
                  <input type="date" className="input" value={timesheetFrom} onChange={(event) => setTimesheetFrom(event.target.value)} aria-label={t('Timesheet from')} />
                  <input type="date" className="input" value={timesheetTo} onChange={(event) => setTimesheetTo(event.target.value)} aria-label={t('Timesheet to')} />
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="workspace-table">
                  <thead><tr><th>{t('Worker')}</th><th>{t('Date')}</th><th>{t('Punch in')}</th><th>{t('Punch out')}</th><th>{t('Paid hours')}</th><th>{t('Status')}</th></tr></thead>
                  <tbody>
                    {timesheetRows.map((row) => (
                      <tr key={row.shiftId}>
                        <td>{row.workerName || row.workerEmail || t('Worker')}</td>
                        <td>{row.date || '-'}</td>
                        <td>{row.punchIn || '-'}</td>
                        <td>{row.punchOut || '-'}</td>
                        <td>{row.paidHours ?? '-'}</td>
                        <td>{row.status || '-'}</td>
                      </tr>
                    ))}
                    {timesheetRows.length === 0 ? <tr><td colSpan={6} className="helper">{t('No timesheets found for this location/date range.')}</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        ) : null}
      </DetailDrawer>

      <FormModal
        title={geofenceLocation ? t('Geofence: {name}', { name: locationName(geofenceLocation) }) : t('Location geofence')}
        description={geofenceOwner?.organization ? `${geofenceOwner.organization.name} / ${canManageLocation(geofenceLocation) ? t('Edit mode') : t('Read-only mode')}` : canManageLocation(geofenceLocation) ? t('Edit mode') : t('Read-only mode')}
        isOpen={Boolean(geofenceLocation)}
        onClose={() => setGeofenceLocation(null)}
        size="xl"
      >
        {geofenceLocation ? (
          <div className="stack">
            <div><h3>{t('Punch geofence')}</h3><p className="helper">{t('Used to validate attendance at the worksite. Changing this boundary does not change the worksite address or routing destination.')}</p></div>
          <Mapper
            initialLocationId={geofenceLocation.id}
            initialOwnerOrgId={ownerOrgId(geofenceLocation)}
            lockedLocation
            readOnly={!canManageLocation(geofenceLocation)}
            compact
            canCreateJobs={canCreateJobs}
            onCreateJobFromLocation={(location) => {
              const scopedLocation = accessibleLocations.find((item) => item.id === location.id)
              if (scopedLocation) onCreateJobFromLocation(scopedLocation)
            }}
            onSaved={async () => {
              const savedLocationId = geofenceLocation.id
              setSelectedLocation((current) => current && current.id === savedLocationId
                ? { ...current, fenceId: current.fenceId || 'saved', geofenceStatus: 'configured' }
                : current)
              setGeofenceLocation((current) => current
                ? { ...current, fenceId: current.fenceId || 'saved', geofenceStatus: 'configured' }
                : current)
              await onRefresh()
              if (geofenceLocation?.id) await loadLocationOperations(geofenceLocation)
            }}
          />
          </div>
        ) : null}
      </FormModal>

      <FormModal
        title={historyLocation ? t('Shift history: {name}', { name: locationName(historyLocation) }) : t('Shift history')}
        description={t('Operational evidence is grouped by scheduled shift and assignment, including completed tracking sessions.')}
        isOpen={Boolean(historyLocation)}
        onClose={() => setHistoryLocation(null)}
        size="xl"
      >
        {historyLocation ? <ShiftHistoryViewer locationId={historyLocation.id} /> : null}
      </FormModal>

      <FormModal
        title={breadcrumbLocation ? t('Live map: {name}', { name: locationName(breadcrumbLocation) }) : t('Live Map / Breadcrumbs')}
        description={t('View latest worker positions and selected worker breadcrumb trails.')}
        isOpen={Boolean(breadcrumbLocation)}
        onClose={closeLiveMap}
        closeOnBackdrop={false}
        size="xl"
      >
        {breadcrumbLocation ? (
          <BreadcrumbTrailViewer
            location={breadcrumbLocation}
            enabled={canUseLiveTracking}
            onLocationUnavailable={closeUnavailableLiveMap}
          />
        ) : null}
      </FormModal>

      <FormModal
        title={breadcrumbGrantedLocation ? t('Live map: {name}', { name: locationName(breadcrumbGrantedLocation) }) : t('Live Map / Breadcrumbs')}
        description={t("View your agency workers' positions and breadcrumb trails at this client location.")}
        isOpen={Boolean(breadcrumbGrantedLocation)}
        onClose={closeLiveMap}
        closeOnBackdrop={false}
        size="xl"
      >
        {breadcrumbGrantedLocation ? (
          <BreadcrumbTrailViewer
            location={breadcrumbGrantedLocation}
            enabled={canUseLiveTracking}
            onLocationUnavailable={closeUnavailableLiveMap}
          />
        ) : null}
      </FormModal>

      <ConfirmDialog
        title={t('Punch out worker')}
        message={punchOutTarget ? t('Punch out {name} at {location}?', { name: punchOutTarget.workerName || punchOutTarget.email || punchOutTarget.phone || t('this worker'), location: selectedLocation ? locationName(selectedLocation) : t('this location') }) : ''}
        isOpen={Boolean(punchOutTarget)}
        confirmLabel={t('Punch out')}
        destructive
        disabled={saving}
        onCancel={() => setPunchOutTarget(null)}
        onConfirm={punchOutWorker}
      />

      {clientLocationGroups.length > 0 ? (
        <section className="network-dashboard-section" style={{ marginTop: '2rem' }}>
          <div>
            <h4>{t('Client Company Locations')}</h4>
            <p className="helper">{t('Granted locations from federated client relationships. You can create jobs at these sites on behalf of the client.')}</p>
          </div>
          {clientLocationGroups.map(({ entry, locations }) => {
            const clientOrgId = entry.companyOrg?.id || entry.relationshipId
            const isHighlighted = highlightClientOrgId === clientOrgId
            return (
              <div
                key={entry.relationshipId}
                ref={(el) => { clientSectionRefs.current[clientOrgId] = el }}
                className={`panel workspace-panel stack${isHighlighted ? ' highlighted' : ''}`}
                style={{ background: 'var(--surface-muted)', marginBottom: '1rem' }}
              >
                <div className="admin-panel-toolbar">
                  <div>
                    <strong>{entry.companyOrg?.name || t('Client company')}</strong>
                    {entry.isFederated ? (
                      <span className="status-pill status-pill--muted" style={{ marginLeft: '0.5rem' }}>{t('Federated')}</span>
                    ) : null}
                    <p className="helper" style={{ margin: 0 }}>
                      {t('Relationship: {id} · Granted sites: {count}', { id: entry.relationshipId, count: locations.length })}
                    </p>
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="workspace-table">
                    <thead>
                      <tr>
                        <th>{t('Location')}</th>
                        <th>{t('Key')}</th>
                        <th>{t('Granted access')}</th>
                        <th>{t('Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {locations.map((loc) => (
                        <tr key={loc.locationId}>
                          <td>{loc.name || loc.locationKey || loc.locationId}</td>
                          <td><span className="admin-chip">{loc.locationKey || '-'}</span></td>
                          <td>
                            <div className="inline-group" style={{ flexWrap: 'wrap', gap: '0.25rem' }}>
                              {loc.canView ? <span className="status-pill status-pill--muted">{t('View')}</span> : null}
                              {loc.canStaff ? <span className="status-pill status-pill--muted">{t(industryLabels.explicitIndustry ? industryLabels.label('workers') : 'Staff')}</span> : null}
                              {loc.canSchedule ? <span className="status-pill status-pill--muted">{t('Schedule')}</span> : null}
                              {loc.canCreateJobs ? <span className="status-pill status-pill--muted">{t('Create jobs')}</span> : null}
                              {loc.canCreateShifts ? <span className="status-pill status-pill--muted">{t('Create shifts')}</span> : null}
                              {loc.canApproveTimesheets ? <span className="status-pill status-pill--muted">{t('Approve timesheets')}</span> : null}
                            </div>
                          </td>
                          <td>
                            <EntityActionMenu>
                              <button
                                type="button"
                                className="btn btn-ghost"
                                disabled={!canUseLiveTracking}
                                onClick={() => openGrantedLiveMap(grantedToAccessible(loc))}
                              >
                                {canUseLiveTracking ? t('Open live map / tracker') : `🔒 ${t('Contact Sales')}`}
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => setHistoryLocation(grantedToAccessible(loc))}
                              >
                                {t('View shift history')}
                              </button>
                              {canCreateJobs && loc.canCreateJobs && onCreateDelegatedJobFromGrantedLocation ? (
                                <button
                                  type="button"
                                  className="btn btn-ghost"
                                  onClick={() => onCreateDelegatedJobFromGrantedLocation(entry, loc)}
                                >
                                  {t('Create job')}
                                </button>
                              ) : null}
                            </EntityActionMenu>
                          </td>
                        </tr>
                      ))}
                      {locations.length === 0 ? (
                        <tr><td colSpan={4} className="helper">{t('No locations have been granted for this relationship.')}</td></tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })}
        </section>
      ) : null}

    </AdminPage>
  )
}
